import { useState } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import tw from 'twin.macro';
import 'styled-components/macro';
import useResizeObserver from 'use-resize-observer';

import { Button } from 'play/atoms/button/Button';

export interface ChallengeActionsProps {
  isCompleted?: boolean;
  isChecking?: boolean;
  canCheck?: boolean;
  canExit?: boolean;
  canSkip?: boolean;
  exitTo?: LinkProps['to'];
  onCheck?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
}

/**
 * Minimal width for the single line layout.
 * Note: this the width of the content, not the box.
 */
const multilineBreakpoint = 240;

export function ChallengeActions({
  isCompleted = false,
  isChecking = false,
  canCheck = false,
  canExit = false,
  canSkip = false,
  exitTo,
  onCheck,
  onNext,
  onSkip,
}: ChallengeActionsProps) {
  const [shouldWrap, setShouldWrap] = useState(false);

  // TODO: There is a tricky way to achieve the desired layout with pure CSS.
  // However, it won't work in Safari until it supports the `text-align-last`
  // CSS property.
  //
  // Example: https://codepen.io/borodean/pen/QWqPExX

  const { ref } = useResizeObserver<HTMLDivElement>({
    onResize({ width }) {
      setShouldWrap(width !== undefined && width < multilineBreakpoint);
    },
  });

  return (
    <div ref={ref} tw="flex px-3 py-2 text-base justify-between" css={shouldWrap && tw`flex-wrap`}>
      {canExit && exitTo && (
        <Link css={shouldWrap ? tw`mr-1 mt-1 order-last` : tw`mr-auto`} to={exitTo} tw="mr-1">
          <Button variant="outline">Exit</Button>
        </Link>
      )}

      <div tw="flex grow" css={[shouldWrap && tw`justify-end`, canExit ? tw`justify-end` : tw`justify-between`]}>
        {canSkip && (
          <Button tw="mr-1" onClick={onSkip} variant="outline">
            Skip
          </Button>
        )}

        {isCompleted ? (
          <Button onClick={onNext}>Next</Button>
        ) : (
          <Button onClick={onCheck} loading={isChecking}>
            {canCheck ? 'Check' : 'Next'}
          </Button>
        )}
      </div>
    </div>
  );
}
